import { apiClient } from "./apiClient";
import {
  ConversationDto,
  MessageDto,
  MessagePageResponse,
  MessageSearchResultDto,
  SendMessageRequest,
} from "../models/MessageModel";

/**
 * REST API service for messaging operations.
 * WebSocket-based sending is handled separately in websocketService.ts.
 */

/**
 * Send a message via REST (fallback when WebSocket is unavailable).
 */
export const sendMessageRest = async (
  request: SendMessageRequest
): Promise<MessageDto> => {
  return apiClient.post<MessageDto>("/api/messages/send", request);
};

/**
 * Get paginated conversation list for the authenticated user.
 */
export const getConversations = async (
  page: number = 0,
  size: number = 20
): Promise<MessagePageResponse<ConversationDto>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });
  return apiClient.get<MessagePageResponse<ConversationDto>>(
    `/api/messages/conversations?${params}`
  );
};

/**
 * Get paginated messages in a conversation.
 * Backend automatically marks messages as read on first page load.
 */
export const getMessages = async (
  conversationId: number,
  page: number = 0,
  size: number = 30
): Promise<MessagePageResponse<MessageDto>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });
  return apiClient.get<MessagePageResponse<MessageDto>>(
    `/api/messages/conversations/${conversationId}?${params}`
  );
};

/**
 * Inbox-wide message search.
 * Searches across ALL of the user's conversations for messages whose
 * content contains the given query string (case-insensitive LIKE).
 *
 * The caller is responsible for ensuring the query has at least 2 characters;
 * the backend will reject shorter queries with a 400.
 */
export const searchInbox = async (
  query: string,
  page: number = 0,
  size: number = 20
): Promise<MessagePageResponse<MessageSearchResultDto>> => {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
    size: size.toString(),
  });
  return apiClient.get<MessagePageResponse<MessageSearchResultDto>>(
    `/api/messages/search?${params}`
  );
};

/**
 * Mark all messages in a conversation as read.
 * Returns the updated total unread count.
 */
export const markConversationAsRead = async (
  conversationId: number
): Promise<{ totalUnread: number }> => {
  return apiClient.put<{ totalUnread: number }>(
    `/api/messages/conversations/${conversationId}/read`
  );
};

/**
 * Soft-delete a single conversation for the authenticated user.
 */
export const deleteConversation = async (
  conversationId: number
): Promise<void> => {
  await apiClient.delete(`/api/messages/conversations/${conversationId}`);
};

/**
 * Bulk soft-delete multiple conversations for the authenticated user.
 * Each conversation is only deleted from the requesting user's side.
 */
export const deleteConversations = async (
  conversationIds: number[]
): Promise<void> => {
  await apiClient.delete("/api/messages/conversations/bulk", {
    conversationIds,
  });
};

/**
 * Soft-delete a single message for the authenticated user.
 */
export const deleteMessage = async (messageId: number): Promise<void> => {
  await apiClient.delete(`/api/messages/${messageId}`);
};

/**
 * Get total unread message count (for header badge).
 */
export const getUnreadCount = async (): Promise<{ totalUnread: number }> => {
  return apiClient.get<{ totalUnread: number }>("/api/messages/unread-count");
};

/**
 * Check if a conversation exists with a specific user.
 * Returns conversationId or -1 if none exists.
 */
export const getConversationWith = async (
  otherUserId: number
): Promise<{ conversationId: number }> => {
  return apiClient.get<{ conversationId: number }>(
    `/api/messages/conversation-with/${otherUserId}`
  );
};